/**
 * Plain-English easement glossary.
 *
 * Every term here is one a homebuyer or agent will hit while reading a title
 * commitment's Schedule B. Each gets its own indexable page so the definition
 * can be linked directly from a report explanation.
 */

export type GlossaryTerm = {
  /** URL slug. */
  slug: string;
  term: string;
  /** One-sentence definition, also used as the meta description seed. */
  short: string;
  /** Two or three plain-English paragraphs. */
  body: string[];
  /** Why it matters to a buyer, in one line. */
  whyItMatters: string;
  /** Slugs of related terms. */
  related: string[];
};

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    slug: "blanket-easement",
    term: "Blanket easement",
    short:
      "An easement recorded across an entire parcel instead of a described strip, so its exact location on the ground is undefined.",
    body: [
      "A blanket easement grants a utility or a neighbor rights over the whole property rather than over a surveyed corridor. They are common in older subdivisions and rural areas, where a utility took a right across a large tract long before it was split into lots.",
      "Because no width or centerline is recorded, you cannot tell from the document alone where equipment may be placed or where you are free to build. In practice the utility's existing lines fix the useful location, and many holders will sign a partial release or a consent letter that pins the easement to the strip they actually occupy.",
    ],
    whyItMatters:
      "A blanket easement on Schedule B means no part of the lot is provably clear until the holder agrees to a defined location in writing.",
    related: ["utility-easement", "schedule-b", "easement-in-gross", "survey"],
  },
  {
    slug: "conservation-easement",
    term: "Conservation easement",
    short:
      "A recorded restriction that permanently limits development on land to protect farmland, habitat, water, or open space.",
    body: [
      "A conservation easement is granted to a land trust or government agency. Instead of giving someone the right to cross your land, it takes away specific rights from you. Usually the right to subdivide, to build beyond a defined envelope, or to clear vegetation.",
      "The restrictions run with the land forever and are enforced by the holder, which typically inspects the property annually. Many were created in exchange for a tax benefit to a prior owner, so a current buyer inherits limits they never negotiated.",
    ],
    whyItMatters:
      "It is the one easement type that can cap what you build permanently, regardless of what local zoning would otherwise allow.",
    related: ["restrictive-covenant", "encumbrance", "setback", "schedule-b"],
  },
  {
    slug: "survey",
    term: "Survey (ALTA/boundary)",
    short:
      "A licensed surveyor's map showing the true boundary lines and where recorded easements actually fall on the ground.",
    body: [
      "A boundary survey locates the corners of your parcel; an ALTA/NSPS survey adds the recorded easements, encroachments, and improvements a title company lists on Schedule B, plotted in their real positions.",
      "Nothing else answers the question that matters before you build. A title commitment tells you an easement exists; a parcel viewer gives you an approximate outline; only a survey tells you the fence post you planned is three feet inside a utility corridor.",
    ],
    whyItMatters:
      "Every plan that depends on exact distances, a fence, pool, shed, or addition, needs a survey before construction, not after.",
    related: ["buildable-envelope", "setback", "encroachment", "schedule-b"],
  },
  {
    slug: "easement",
    term: "Easement",
    short: "A legal right for someone else to use part of your land for a specific purpose.",
    body: [
      "An easement gives another person, a utility, or a government body the right to use a defined part of your property for a defined purpose. You still own the land. You simply cannot block the use the easement protects.",
      "Most easements are recorded, which means they appear in the county land records and travel with the property when it sells. A few are created by long use or by necessity and never appear on paper at all.",
    ],
    whyItMatters:
      "An easement can decide where you may build, fence, plant, or pave. Often on the most usable part of a lot.",
    related: ["appurtenant-easement", "easement-in-gross", "right-of-way", "schedule-b"],
  },
  {
    slug: "appurtenant-easement",
    term: "Appurtenant easement",
    short: "An easement that benefits a neighboring parcel rather than a person or company.",
    body: [
      "An appurtenant easement links two parcels: one is burdened (the servient estate) and one is benefited (the dominant estate). A shared driveway that lets the rear lot reach the street is the classic example.",
      "Because the right belongs to the land and not to a person, it passes automatically to the next owner of the benefited parcel. Selling either property does not end it.",
    ],
    whyItMatters:
      "If your lot is the burdened one, a neighbor has a permanent right to cross it. And you cannot revoke that right by selling.",
    related: ["easement", "easement-in-gross", "dominant-estate", "servient-estate"],
  },
  {
    slug: "easement-in-gross",
    term: "Easement in gross",
    short: "An easement held by a person or company rather than by a neighboring parcel.",
    body: [
      "Utility easements are the most common easement in gross. The electric, gas, water, or telecom provider holds the right directly, and there is no benefited parcel next door.",
      "Commercial easements in gross are usually transferable, so a utility that merges or sells its network passes the right along with it.",
    ],
    whyItMatters:
      "Utility easements frequently run the full length of a side or rear yard and typically prohibit permanent structures inside them.",
    related: ["easement", "utility-easement", "appurtenant-easement"],
  },
  {
    slug: "utility-easement",
    term: "Utility easement",
    short: "A strip of land reserved so a utility can install and maintain its lines.",
    body: [
      "Utility easements cover overhead power, buried electric and fiber, gas mains, water and sewer lines, and the access needed to repair them. Widths are commonly 5 to 20 feet, though transmission corridors can be far wider.",
      "Inside the strip, the utility can dig, trim vegetation, and bring in equipment. Fences, sheds, pools, and mature trees planted there may be removed at your cost.",
    ],
    whyItMatters:
      "A utility corridor across the buildable part of a lot can quietly remove the spot you were planning to build on.",
    related: ["easement-in-gross", "right-of-way", "encroachment"],
  },
  {
    slug: "right-of-way",
    term: "Right-of-way",
    short: "The right to travel across land, and the strip of land set aside for that travel.",
    body: [
      "Public rights-of-way carry roads, sidewalks, and the utilities beneath them. The strip usually reaches well past the visible pavement, so part of what looks like your front yard may sit inside it.",
      "Private rights-of-way serve one or a few parcels, typically as a shared drive or access lane recorded in a deed.",
    ],
    whyItMatters:
      "Setbacks are measured from the right-of-way line, not from the curb, so it directly shapes where a house or addition can go.",
    related: ["easement", "setback", "utility-easement"],
  },
  {
    slug: "prescriptive-easement",
    term: "Prescriptive easement",
    short: "A use right earned by open, continuous, unpermitted use over a period set by state law.",
    body: [
      "When someone uses part of a property openly, continuously, and without permission for the full statutory period, courts can recognize a permanent right to keep doing so. The period ranges from 5 to 20 years depending on the state.",
      "Permission defeats the claim. A written license, even a simple signed note, generally stops the clock.",
    ],
    whyItMatters:
      "Prescriptive rights never appear in a title search, so a long-used neighbor path can be a real burden nobody disclosed.",
    related: ["easement", "adverse-possession", "easement-by-necessity"],
  },
  {
    slug: "easement-by-necessity",
    term: "Easement by necessity",
    short: "Access granted by a court when a parcel would otherwise be landlocked.",
    body: [
      "When a parcel is split in a way that leaves one piece with no legal route to a public road, courts imply a right of access across the retained land. The rule exists because land with no way in is effectively unusable.",
      "The route must be reasonably necessary, not merely convenient, and it usually follows the least damaging path.",
    ],
    whyItMatters:
      "A neighbor with no other access may hold a right across your lot even though nothing was ever recorded.",
    related: ["prescriptive-easement", "right-of-way", "easement"],
  },
  {
    slug: "schedule-b",
    term: "Schedule B",
    short: "The section of a title commitment listing what the policy will not cover.",
    body: [
      "Schedule B-I lists the requirements you must satisfy before the policy issues. Schedule B-II lists exceptions: the easements, restrictions, liens, and encroachments the insurer refuses to insure against.",
      "Most easement surprises are buried in Schedule B-II, often written as a reference to a recorded document rather than as a plain description of what it does.",
    ],
    whyItMatters:
      "Schedule B is where the actual limits on your property live, and it is the section most buyers never read.",
    related: ["title-commitment", "easement", "encumbrance"],
  },
  {
    slug: "title-commitment",
    term: "Title commitment",
    short: "The insurer's written promise to issue a title policy, subject to listed conditions.",
    body: [
      "A commitment describes the parcel, names the insured, states the policy amount, and sets out requirements and exceptions. It is issued before closing so problems can be resolved while there is still leverage.",
      "It is not a survey and does not show where anything is on the ground. It only recites what the records say.",
    ],
    whyItMatters:
      "It is the single best document for finding easement problems before you are contractually committed.",
    related: ["schedule-b", "encumbrance", "title-policy-exception"],
  },
  {
    slug: "title-policy-exception",
    term: "Title policy exception",
    short: "A specific risk the title insurer refuses to cover.",
    body: [
      "Exceptions can be standard (matters a survey would reveal, rights of parties in possession) or specific (a named utility easement recorded at a stated book and page).",
      "Some standard exceptions can be removed by delivering a current survey or an owner's affidavit.",
    ],
    whyItMatters:
      "Every exception is a risk that shifts from the insurer back to you.",
    related: ["schedule-b", "title-commitment", "encumbrance"],
  },
  {
    slug: "encumbrance",
    term: "Encumbrance",
    short: "Any claim or right held by someone other than the owner that affects the property.",
    body: [
      "Encumbrances include easements, liens, mortgages, leases, restrictive covenants, and judgments. Some affect use, some affect title, and some affect both.",
      "An encumbrance does not stop a sale. It simply travels with the property unless it is released.",
    ],
    whyItMatters:
      "The full encumbrance list defines what you are actually buying.",
    related: ["easement", "schedule-b", "restrictive-covenant"],
  },
  {
    slug: "encroachment",
    term: "Encroachment",
    short: "A structure that extends onto land it does not belong on.",
    body: [
      "A fence three feet over the line, a garage eave crossing a setback, or a driveway paved into a utility strip are all encroachments. They show up on surveys, not in title records.",
      "Left alone long enough, an encroachment can mature into a prescriptive right or an adverse possession claim.",
    ],
    whyItMatters:
      "Unresolved encroachments become the buyer's problem at closing and can block a future sale or permit.",
    related: ["prescriptive-easement", "setback", "adverse-possession"],
  },
  {
    slug: "setback",
    term: "Setback",
    short: "The minimum distance a structure must sit from a property line or right-of-way.",
    body: [
      "Setbacks are set by local zoning and sometimes by subdivision covenants. Front, side, and rear values usually differ, and corner lots often carry two front setbacks.",
      "Easements stack on top of setbacks. A 10-foot side setback with a 10-foot utility easement inside it does not add up to 20 feet, but a wider easement can push the buildable line further in.",
    ],
    whyItMatters:
      "Setbacks plus easements define the buildable envelope. The ground you may actually build on.",
    related: ["right-of-way", "utility-easement", "buildable-envelope"],
  },
  {
    slug: "buildable-envelope",
    term: "Buildable envelope",
    short: "The part of a lot left over once setbacks, easements, and corridors are removed.",
    body: [
      "Start with the parcel boundary, subtract every setback, then subtract every easement and right-of-way that prohibits structures. What remains is where a house, addition, pool, or shop can legally sit.",
      "On irregular or corridor-crossed lots the envelope can be a small fraction of the recorded acreage.",
    ],
    whyItMatters:
      "Two lots of identical acreage can have wildly different envelopes, and only one may fit your plan.",
    related: ["setback", "utility-easement", "right-of-way"],
  },
  {
    slug: "dominant-estate",
    term: "Dominant estate",
    short: "The parcel that benefits from an appurtenant easement.",
    body: [
      "The dominant estate holds the right. For example, the rear lot entitled to cross a shared drive to reach the road.",
      "The benefit passes automatically with ownership of the parcel.",
    ],
    whyItMatters:
      "Buying the dominant estate means buying guaranteed access; confirm the recorded route matches the one in use.",
    related: ["servient-estate", "appurtenant-easement", "easement"],
  },
  {
    slug: "servient-estate",
    term: "Servient estate",
    short: "The parcel burdened by an easement.",
    body: [
      "The servient owner keeps full ownership and may use the strip in any way that does not unreasonably interfere with the easement holder's rights.",
      "Maintenance duties depend on the easement document and, absent one, on state default rules.",
    ],
    whyItMatters:
      "If your lot is servient, expect limits on fencing, planting, grading, and building inside the burdened strip.",
    related: ["dominant-estate", "appurtenant-easement", "easement"],
  },
  {
    slug: "restrictive-covenant",
    term: "Restrictive covenant",
    short: "A recorded private rule limiting how land in a subdivision may be used.",
    body: [
      "Covenants are written by developers and enforced by neighbors or an HOA. They govern building materials, outbuildings, home businesses, parking, and often architectural approval.",
      "They are private contract terms, so a use that zoning permits can still be prohibited.",
    ],
    whyItMatters:
      "Covenants routinely restrict more than local zoning does, and they are enforceable against you.",
    related: ["encumbrance", "setback", "schedule-b"],
  },
  {
    slug: "adverse-possession",
    term: "Adverse possession",
    short: "A claim to ownership, not just use, earned by occupying land for the statutory period.",
    body: [
      "Adverse possession requires possession that is actual, open, notorious, exclusive, continuous, and hostile for the state's statutory term. Some states also require paying the taxes or holding color of title.",
      "It transfers title itself, which makes it far more serious than a prescriptive easement.",
    ],
    whyItMatters:
      "A long-standing fence in the wrong place can eventually move the legal boundary.",
    related: ["prescriptive-easement", "encroachment"],
  },
];

export function findGlossaryTerm(slug: string | undefined): GlossaryTerm | undefined {
  if (!slug) return undefined;
  const normalized = slug.trim().toLowerCase();
  return GLOSSARY_TERMS.find((t) => t.slug === normalized);
}

export const GLOSSARY_TERMS_SORTED = [...GLOSSARY_TERMS].sort((a, b) =>
  a.term.localeCompare(b.term),
);
